import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QuickEditForm from "./QuickEditForm";
import styles from "./BlogPostRow.module.css";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  categoryIds?: string[];
  categoryName: string;
  categoryId: string;
  tags: string[];
  author: {
    name: string;
    avatar: string;
  };
  publishDate: string;
  readTime: string;
  metaTitle: string;
  metaDescription: string;
  views: number;
  featured: boolean;
  status?: "draft" | "published" | "trashed";
}

interface BlogPostRowProps {
  post: BlogPost;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onTrash: (id: string) => void;
  onRestore: (id: string) => void;
  onDeletePermanently: (id: string) => void;
  onQuickEdit: (id: string) => void;
  isQuickEditOpen: boolean;
  onUpdatePost: (updatedPost: Partial<BlogPost>) => void;
}

const BlogPostRow: React.FC<BlogPostRowProps> = ({
  post,
  isSelected,
  onSelect,
  onTrash,
  onRestore,
  onDeletePermanently,
  onQuickEdit,
  isQuickEditOpen,
  onUpdatePost,
}) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case "published":
        return (
          <span className={`${styles.statusBadge} ${styles.statusPublished}`}>
            Published
          </span>
        );
      case "draft":
        return (
          <span className={`${styles.statusBadge} ${styles.statusDraft}`}>
            Draft
          </span>
        );
      case "trashed":
        return (
          <span className={`${styles.statusBadge} ${styles.statusTrashed}`}>
            Trashed
          </span>
        );
      default:
        return (
          <span className={`${styles.statusBadge} ${styles.statusPublished}`}>
            Published
          </span>
        );
    }
  };

  const handleEdit = () => {
    navigate(`/app/blog/${post.slug}/edit`);
  };

  const handleView = () => {
    window.open(`https://mamimarkets.com/blog/${post.slug}`, "_blank");
  };

  return (
    <>
      <tr
        className={`blog-post-row ${isHovered ? "hovered" : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <td className="col-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(post.id)}
          />
        </td>
        <td className="col-title">
          <div className="title-content">
            {post.featuredImage ? (
              <img
                className="post-thumbnail"
                src={post.featuredImage}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const placeholder = target.nextElementSibling as HTMLElement;
                  if (placeholder) placeholder.style.display = "flex";
                }}
                alt={post.title}
              />
            ) : null}
            <div
              className={`post-thumbnail-placeholder ${
                post.featuredImage
                  ? styles.thumbnailPlaceholderHidden
                  : styles.thumbnailPlaceholder
              }`}
            >
              <span>No Image</span>
            </div>
            <div className="title-text">
              <Link to={`/app/blog/${post.slug}/edit`} className="post-title">
                {post.title}
              </Link>
              {isHovered && (
                <div className="hover-actions">
                  <button className="action-link" onClick={handleEdit}>
                    Edit
                  </button>
                  <span className="action-separator">|</span>
                  <button
                    className="action-link"
                    onClick={() => onQuickEdit(post.id)}
                  >
                    Quick Edit
                  </button>
                  {post.status === "published" && (
                    <>
                      <span className="action-separator">|</span>
                      <button className="action-link" onClick={handleView}>
                        View
                      </button>
                    </>
                  )}
                  {post.status !== "trashed" && (
                    <>
                      <span className="action-separator">|</span>
                      <button
                        className="action-link action-delete"
                        onClick={() => onDeletePermanently(post.id)}
                        title="Permanently delete this post"
                      >
                        Delete Permanently
                      </button>
                    </>
                  )}
                  {post.status === "trashed" && (
                    <>
                      <span className="action-separator">|</span>
                      <button
                        className="action-link"
                        onClick={() => onRestore(post.id)}
                      >
                        Restore
                      </button>
                      <span className="action-separator">|</span>
                      <button
                        className="action-link action-delete"
                        onClick={() => onDeletePermanently(post.id)}
                      >
                        Delete Permanently
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="col-status">{getStatusBadge(post.status)}</td>
        <td className="col-category">
          <div className="categories-container">
            {post.categoryName ? (
              <span className="category-badge">{post.categoryName}</span>
            ) : (
              <span className="category-badge">No category</span>
            )}
          </div>
        </td>
        <td className="col-author">{post.author.name}</td>
        <td className="col-date">{formatDate(post.publishDate)}</td>
      </tr>
      {isQuickEditOpen && (
        <tr className="quick-edit-row">
          <td colSpan={6}>
            <QuickEditForm
              post={post}
              onSave={(updates: Partial<BlogPost>) =>
                onUpdatePost({ id: post.id, ...updates })
              }
              onCancel={() => onQuickEdit(post.id)}
            />
          </td>
        </tr>
      )}
    </>
  );
};

export default BlogPostRow;
